import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Button, CardContent, Card, Chip, useTheme, useMediaQuery, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ThemeProvider } from '@mui/material/styles';
import { Logo, LogoDarkMode, DotPattern } from '../assets/images';
import FlipCountdown from '../components/FlipCountdown';
import PurchaseModal from '../components/PurchaseModal';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CssBaseline from '@mui/material/CssBaseline';
import professionalIcon from "../assets/images/professional-icon.svg";
import webinarauditlIcon from "../assets/images/webinar-audit-icon.svg";
import g2Icon from "../assets/images/g2-img.svg";
import badgesIcon from "../assets/images/badges.svg";

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
    <path d="M18.1673 9.12849C18.5479 10.9962 18.2767 12.938 17.3989 14.63C16.5211 16.322 15.0897 17.6619 13.3436 18.4263C11.5975 19.1906 9.64203 19.3333 7.80342 18.8305C5.96482 18.3276 4.35417 17.2097 3.24007 15.663C2.12597 14.1164 1.57577 12.2345 1.68123 10.3313C1.78668 8.4281 2.5414 6.61857 3.81955 5.20447C5.09769 3.79037 6.82199 2.85718 8.70489 2.56054C10.5878 2.26389 12.5155 2.62171 14.1665 3.57432" stroke="#00C950" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M7.5 9.96224L10 12.4622L18.3333 4.12891" stroke="#00C950" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
);


const LiveAuditWorkshopOffer: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlan, setModalPlan] = useState<'professionalBundle' | 'auditBundle'>('professionalBundle');
  const [isNonAttendee, setIsNonAttendee] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  // Set countdown target date to 22 Oct 2025, 11:59 PM PST
  const countdownTargetDate = new Date('2025-10-22T23:59:00-07:00');

  const handleOpenModal = (plan: 'professionalBundle' | 'auditBundle') => {
    setModalPlan(plan);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenVideoModal = (url: string) => {
    setVideoUrl(url);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setVideoUrl('');
  };

  const planData = {
    professionalBundle: {
      title: "Professional+",
      price: "$1,297",
      modalTile: "Sign Up and Claim Your Professional Bundle!"

    },
    auditBundle: {
      title: "Audit",
      price: "$1000",
      modalTile: "Unlock the Webinar Funnel Audit Bundle today!"
    }
  };

  function CountdownTimer() {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
      const calculateTimeLeft = () => {
        const now = new Date();
        const difference = countdownTargetDate.getTime() - now.getTime();

        if (difference > 0) {
          const result = {
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60)
          };
          return result;
        } else {
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
      };

      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft());
      }, 1000);

      // Initial calculation
      setTimeLeft(calculateTimeLeft());

      return () => clearInterval(timer);
    }, [countdownTargetDate]);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 'bold',
            fontSize: '48px',
            lineHeight: '60px',
            color: '#fe7a49',
            whiteSpace: 'pre',
            mt: { xs: 2, md: 2 }
          }}
        >
          {String(timeLeft.days).padStart(2, '0')}  :  {String(timeLeft.hours).padStart(2, '0')} :  {String(timeLeft.minutes).padStart(2, '0')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontStyle: 'italic',
            fontSize: '19px',
            lineHeight: '25px',
            width: '277px',
            whiteSpace: 'pre-wrap',
            marginBottom: '20px',
          }}
        >
          {`   Days           Hours      Minutes`}
        </Typography>
      </Box>
    );
  }

  // card component
  function FeatureCard({ title, description, fixNumber }: { title: string; description: string; fixNumber: string }) {
    return (
      <Card sx={{ height: '100%', maxWidth: 532, boxShadow: 'none', }}>
        <CardContent sx={{ p: 3, boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, boxShadow: 'none', }}>
            <img src={professionalIcon} alt="" />
            <Chip
              label={fixNumber}
              sx={{
                backgroundColor: '#fe7a49',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
                borderRadius: 2,
                px: 0.2,
                py: 0,
                boxShadow: 'none',
                border: 'none'
              }}
            />
          </Box>

          <Typography
            variant="h3"
            sx={{
              color: '#fe7a49',
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.25rem', sm: '1.375rem' }
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0, 0, 0, 0.8)',
              lineHeight: 1.56,
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        color: "#1f2937",
        fontFamily: "Inter",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >

      <Box
        sx={{
          width: "100vw",
          color: "#000000",
          fontFamily: "Inter",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 4,
          mb: 5,
        }}
      >
        {/* Header with Logo */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <img
            src={Logo}
            alt="logo"
            style={{ cursor: "pointer", maxWidth: "100%", height: "32px" }}
            onClick={() => window.open("https://easywebinar.com/", "_blank")}
          />
        </Box>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "32px" },
            fontWeight: 700,
            mb: { xs: 1, lg: 2 },
            lineHeight: 1.2,
            fontFamily: "Inter, sans-serif",
          }}
        >
          Professional Bundle
        </Typography>

        <Typography
          component="h3"
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            maxWidth: "779px",
            fontSize: {
              xs: "14px",   // mobile
              sm: "16px",   // small screens
              md: "18px",   // medium
              lg: "20px" // large+ (original size)
            },
            lineHeight: {
              xs: "18px",
              sm: "20px",
              md: "22px",
              lg: "25px"
            },
            letterSpacing: "0",
            textAlign: "center",
            verticalAlign: "middle",
            mb: { xs: 1, md: 1 }, // optional margin bottom
            px: 2,
          }}
        >
          The all-in-one solution that includes the <span style={{ fontWeight: 'bold' }}>tech, training, and expert guidance </span>
          you need to build webinars that actually convert.
        </Typography>

        {/* Limited Time Offer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 2,
          }}
        >
          <Typography
            sx={{
              color: '#1a202c',
              fontSize: { xs: '16px', md: '20px' },
              fontWeight: '500',
              margin: 0,
            }}
          >
            ⏰ Limited-Time Offer
          </Typography>
        </Box>

        {/* Countdown Timer */}
        {/* <Box sx={{ mb: { xs: 4, md: 8 } }}>
        <Box
          sx={{
            '& > div > div > div > div': {
              width: { sm: '100px', md: '120px' },
              height: { sm: '110px', md: '130px' },
              '& .MuiTypography-root': {
                fontSize: { sm: '40px', md: '48px' },
              }
            },
            animation: "float 3s ease-in-out infinite",
            "@keyframes float": {
              "0%, 100%": {
                transform: "translateY(0px)",
              },
              "50%": {
                transform: "translateY(-10px)",
              },
            },
          }}
        >
          <FlipCountdown
            targetDate={countdownTargetDate}
            onComplete={() => {
              console.log('Countdown completed!');
            }}
          />
        </Box>
      </Box> */}
        <CountdownTimer />
        <Button
          variant="contained"
          sx={{
            bgcolor: "#FF6B35",
            color: "white",
            fontSize: { xs: "16px", sm: "18px", md: "20px" },
            fontWeight: 700,
            px: 6,
            py: 1,
            minHeight: { xs: "48px" },
            textTransform: "none",
            boxShadow: "0 8px 32px rgba(255, 107, 53, 0.4)",
            border: "none",
            transition: "all 0.3s ease-in-out",
            transform: "translateY(0)",
            "&:hover": {
              bgcolor: "#E55A2B",
              boxShadow: "0 12px 40px rgba(255, 107, 53, 0.6)",
              transform: "translateY(-3px)",
            },
            "&:active": {
              transform: "translateY(-1px)",
            },
            fontFamily: "Inter, sans-serif",
          }}
          onClick={() => {
            handleOpenModal('professionalBundle');
          }}
        >
          Start today!
        </Button>
      </Box>


      {/* bonus with three section */}

      {/* Pricing section */}
      <Box
        sx={{
          backgroundColor: '#000000',
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { sm: 1, xs: 2 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Pricing Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 4, sm: 6, md: 12 },
            mb: { xs: 2 },
            px: { xs: 2, sm: 0 },
          }}
        >
          {/* Left Side - Original Price and Discount Tag */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 'fit-content',
            }}
          >
            {/* Original Price - Struck Through */}
            <Typography
              sx={{
                fontSize: { xs: '28px', sm: '32px', md: '34px' },
                color: 'white',
                textDecoration: 'line-through',
                fontWeight: '400',
                mb: { xs: 1.5, md: 2 },
                lineHeight: 1,
              }}
            >
              $5,177
            </Typography>

            {/* Discount Tag */}
            <Box
              sx={{
                backgroundColor: '#DCFCE7',
                color: '#016630',
                px: { xs: 3, sm: 2 },
                py: 1,
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '11px', sm: '14px', md: '20px' },
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: { xs: '0.5px', sm: '1px' },
                }}
              >
                OVER 75% OFF
              </Typography>
            </Box>
          </Box>

          {/* Right Side - Current Price and Today Only */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 'fit-content',
            }}
          >
            {/* Current Price */}
            <Typography
              sx={{
                fontSize: { xs: '42px', sm: '56px', md: '61px' },
                color: '#0E9E4D',
                fontWeight: '700',
                mb: { xs: 1.5, md: 2 },
                lineHeight: 1,
              }}
            >
              $1,297
            </Typography>

            {/* Today Only Tag */}
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '16px', md: '23px' },
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: { xs: '0.5px', sm: '1px' },
              }}
            >
              TODAY ONLY
            </Typography>
          </Box>
        </Box>

        {/* Tagline */}
        <Typography
          sx={{
            fontSize: { xs: '13px', sm: '16px', md: '22px' },
            color: 'white',
            textAlign: 'center',
            maxWidth: { xs: '90%' },
            px: { xs: 2, sm: 0 },
            lineHeight: { xs: 1.4, md: 1.5 },
            mb: 7
          }}
        >
          Everything you need to create webinars that actually convert.
        </Typography>

        {/* What You Get Section */}
        <Box
          sx={{
            backgroundColor: '#FFF7F7',
            borderRadius: '24px',
            p: { xs: 2, md: 6 },
            maxWidth: '1356px',
            width: '100%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            mx: { xs: 0, sm: 4 },
          }}
        >
          {/* What You Get Title */}
          <Typography
            sx={{
              fontSize: { xs: '24px', md: '32px', lg: '36px' },
              fontWeight: 'bold',
              color: '#1a202c',
              textAlign: 'center',
              mb: 4,
            }}
          >
            What You Get:
          </Typography>

          {/* Module 1 - The Sales Experience */}
          <Box sx={{ mb: 4, pb: 2, borderBottom: '2px solid #DBDADA' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: { xs: '16px', md: '24px', lg: '28px' },
                  fontWeight: 'bold',
                  color: '#FE7A49',
                }}
              >
                The Sales Experience (Module 1)
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#FE7A49',
                  color: 'white',
                  px: { xs: 1.2, md: 2 },
                  py: { xs: 0.5, md: 1 },
                  borderRadius: '35px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '20px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  $1,197 Value
                </Typography>
              </Box>
            </Box>
            <Box >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  Complete sales funnel framework
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  High-converting webinar scripts
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  Proven sales psychology techniques
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* One on One Funnel Audit */}
          <Box sx={{ mb: 4, pb: 2, borderBottom: '2px solid #DBDADA' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: { xs: '16px', md: '24px', lg: '28px' },
                  fontWeight: 'bold',
                  color: '#FE7A49',
                }}
              >
                One-on-One Funnel Audit
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#FE7A49',
                  color: 'white',
                  px: { xs: 1.2, md: 2 },
                  py: { xs: 0.5, md: 1 },
                  borderRadius: '35px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '20px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  $2000 Value
                </Typography>
              </Box>
            </Box>
            <Box >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  Personalized funnel analysis
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  Custom optimization recommendations
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  60-minute strategy session
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* One year EasyWebinar Pro */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: { xs: '16px', md: '24px', lg: '28px' },
                  fontWeight: 'bold',
                  color: '#FE7A49',
                }}
              >
                One year EasyWebinar Professional Plan
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#FE7A49',
                  color: 'white',
                  px: { xs: 1.2, md: 2 },
                  py: { xs: 0.5, md: 1 },
                  borderRadius: '35px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '20px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  $1,980 Value
                </Typography>
              </Box>
            </Box>
            <Box >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: "10px", gap: 1 }}>
                <CheckCircleIcon />
                <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '16px', md: "20px" } }}>
                  Premium access to EasyCast + EasyCRM
                </Typography>
              </Box>
              <Box sx={{ pl: 3, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <CheckCircleIcon />
                  <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '14px', md: "18px" } }}>
                    Story-to-Sales Script
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <CheckCircleIcon />
                  <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '14px', md: "18px" } }}>
                    High-Converting Email Sequences
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <CheckCircleIcon />
                  <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '14px', md: "18px" } }}>
                    Landing Page Frameworks
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <CheckCircleIcon />
                  <Typography sx={{ color: '#0A0A0A', fontSize: { xs: '14px', md: "18px" } }}>
                    Webinar Checklist
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>


        </Box>
        {/* New Pricing and CTA Section */}
        <Box
          sx={{
            backgroundColor: '#000000',
            borderRadius: '16px',
            p: { xs: 4, md: 6 },
            mt: 4,
            textAlign: 'center',
          }}
        >
          {/* Pricing Comparison */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 },
              mb: { xs: 4, md: 6 },
              flexWrap: 'wrap',
            }}
          >
            {/* Valued Total */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '14px', md: '18px' },
                  fontWeight: '600',
                  mb: 1,
                }}
              >
                Valued Total:
              </Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '24px', md: '32px' },
                  textDecoration: 'line-through',
                  opacity: 0.9,
                  textAlign: 'center',
                }}
              >
                $5,177
              </Typography>
            </Box>

            {/* Arrow */}
            <ArrowForwardIcon
              sx={{
                color: 'white',
                fontSize: { xs: '32px', md: '70px' },
                mx: { xs: 2, md: 3 },
                rotate: { xs: '90deg', sm: '0deg' },
              }}
            />

            {/* Today */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '16px', md: '23px' },
                  fontWeight: '600',
                  mb: 0.5,
                }}
              >
                Today:
              </Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '32px', md: '48px' },
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                $1,297
              </Typography>
            </Box>
          </Box>

          {/* Combined Action Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'center',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                },
              }}
              onClick={() => handleOpenModal('professionalBundle')}
            >
              {/* Left Section - OVER 75% OFF */}
              <Box
                sx={{
                  backgroundColor: '#383838a6',
                  px: { xs: 4, md: 6 },
                  py: { xs: 1, md: '12px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '140px', md: '180px' },
                  borderRadius: '44px',
                  right: { xs: 0, sm: '-34px' },
                  bottom: { xs: -6, sm: '0' },
                  position: 'relative',
                }}
              >
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { xs: '16px', md: '20px' },
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                  }}
                >
                  OVER 75% OFF
                </Typography>
              </Box>

              {/* Right Section - Get Professional Bundle */}
              <Box
                sx={{
                  backgroundColor: '#FF7F50',
                  px: { xs: 4, md: 6 },
                  py: { xs: 2, md: "18px" },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '200px', md: '280px' },
                  borderRadius: '44px',
                  zIndex: 1,

                  '&:hover': {
                    backgroundColor: '#e55a2b',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { xs: '16px', md: '23px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                    letterSpacing: '0.3px',
                  }}
                >
                  Get Professional Bundle - $1,297
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Three Column Features Section */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          width: '100%',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={0}>
            {/* Column 1: The Sales Experience (Module 1) */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: { xs: 'none', md: '1px solid #e2e8f0' },
                  borderBottom: { xs: '1px solid #e2e8f0', md: 'none' },
                  pr: { xs: 0, md: 4 },
                  pb: { xs: 4, md: 0 },
                  pt: "24px",
                }}
              >
                {/* Title */}
                <Typography
                  sx={{
                    fontSize: { xs: '20px', md: '24px' },
                    fontWeight: 'bold',
                    color: '#FE7A49',
                    mb: 3,
                    lineHeight: 1.3,
                    textAlign: 'center',
                  }}
                >
                  The Sales Experience (Module 1)
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    color: '#000000',
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  A complete training system with step-by-step video lessons, companion written resources, and guided community access—all designed to help you build a leak-proof, authentic sales experience.
                </Typography>

                {/* You'll get section */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', md: '18px' },
                      fontWeight: 'bold',
                      color: '#1a202c',
                      mb: 2,
                    }}
                  >
                    You'll get:
                  </Typography>

                  {/* Feature 1 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        A Message That Lands:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        Clarify exactly who you serve and why, so your audience sees you as the obvious choice.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature 2 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        A Story That Connects:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        Transform your journey and client wins into sales assets that feel natural, not pushy.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature 3 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        Content That Converts:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        Implement simple frameworks that keep your message consistent, authentic, and designed to move people to action.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Column 2: Personal Guidance & Coaching */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: { xs: 'none', md: '1px solid #e2e8f0' },
                  borderBottom: { xs: '1px solid #e2e8f0', md: 'none' },
                  px: { xs: 0, md: 4 },
                  mt: { xs: 3, md: 0 },
                  pt: "24px",
                }}
              >
                {/* Title */}
                <Typography
                  sx={{
                    fontSize: { xs: '20px', md: '24px' },
                    fontWeight: 'bold',
                    color: '#FE7A49',
                    mb: 3,
                    lineHeight: 1.3,
                    textAlign: 'center',
                  }}
                >
                  Personal Guidance & Coaching
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    color: '#4a5568',
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  A tailored coaching experience designed to take the guesswork out of selling. You'll get private expert eyes on your funnel, actionable feedback you can implement right away, and ongoing support to keep you accountable as you make changes.
                </Typography>

                {/* You'll get section */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', md: '18px' },
                      fontWeight: 'bold',
                      color: '#1a202c',
                      mb: 2,
                    }}
                  >
                    You'll get:
                  </Typography>

                  {/* Feature 1 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        One-on-One Funnel Audit:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        A private strategy call to review your story, offer, and presentation flow. You'll walk away knowing exactly what's working, what's costing you sales, and how to fix it fast.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature 2 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        Written Performance Roadmap:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        A detailed plan built from AI analysis and expert recommendations—so you have a clear checklist of improvements, not just vague advice.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature 3 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        Two Live Group Coaching/Q&A Calls:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        Direct access to expert coaching in a group setting where you can ask questions, troubleshoot challenges, and learn from others going through the same process.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Column 3: Execution Tools & Assets */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  pl: { xs: 0, md: 4 },
                  mt: { xs: 4, md: 0 },
                  pt: "24px",
                }}
              >
                {/* Title */}
                <Typography
                  sx={{
                    fontSize: { xs: '20px', md: '24px' },
                    fontWeight: 'bold',
                    color: '#FE7A49',
                    mb: 3,
                    lineHeight: 1.3,
                    textAlign: 'center',
                  }}
                >
                  Execution Tools & Assets
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    color: '#4a5568',
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  12 months of EasyWebinar Professional, giving you premium access to the tools and templates you need to move from planning to execution without the tech headaches. You'll have everything in one place to run professional, high-converting webinars from day one.
                </Typography>

                {/* You'll get section */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', md: '18px' },
                      fontWeight: 'bold',
                      color: '#1a202c',
                      mb: 2,
                    }}
                  >
                    You'll get:
                  </Typography>

                  {/* Feature 1 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        EasyWebinar Professional Access:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                        }}
                      >
                        12 months of our top-tier platform, including EasyCast for seamless streaming and EasyCRM for tracking and nurturing your leads.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature 2 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box width="20px" height="20px"><CheckCircleIcon /></Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 'bold',
                          color: '#1a202c',
                          mb: 0.5,
                        }}
                      >
                        Pre-Built Funnel Templates:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', md: '15px' },
                          color: '#4a5568',
                          lineHeight: 1.5,
                          mb: 1,
                        }}
                      >
                        Done-for-you assets you can plug in and launch fast:
                      </Typography>

                      {/* Nested bullet points */}
                      <Box sx={{ pl: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box
                            sx={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: '#1a202c',
                              mt: 1,
                              mr: 1.5,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: '12px', md: '14px' },
                              color: '#4a5568',
                              lineHeight: 1.4,
                            }}
                          >
                            Story-to-Sales Script to guide your presentation flow
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box
                            sx={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: '#1a202c',
                              mt: 1,
                              mr: 1.5,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: '12px', md: '14px' },
                              color: '#4a5568',
                              lineHeight: 1.4,
                            }}
                          >
                            High-Converting Email Sequences to boost registrations and follow-up sales
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box
                            sx={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: '#1a202c',
                              mt: 1,
                              mr: 1.5,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: '12px', md: '14px' },
                              color: '#4a5568',
                              lineHeight: 1.4,
                            }}
                          >
                            Landing Page Frameworks to capture and convert more leads
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box
                            sx={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: '#1a202c',
                              mt: 1,
                              mr: 1.5,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: '12px', md: '14px' },
                              color: '#4a5568',
                              lineHeight: 1.4,
                            }}
                          >
                            Webinar Checklist so nothing falls through the cracks on event day
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Momentum Call Bonus Section */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          width: '100%',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            {/* Fast-Action Bonus Tag */}
            <Box
              sx={{
                backgroundColor: '#000000',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: '20px',
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '12px', md: '14px' },
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                }}
              >
                Fast-Action Bonus
              </Typography>
            </Box>

            {/* Main Title */}
            <Typography
              sx={{
                fontSize: { xs: '24px', md: '36px' },
                fontWeight: 'bold',
                color: '#FF7F50',
                mb: 4,
                lineHeight: 1.2,
              }}
            >
              Momentum Call (Fast-Action Bonus)
            </Typography>

            {/* Introductory Paragraphs */}
            <Typography
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#000000',
                mb: 3,
                lineHeight: 1.6,
                maxWidth: '800px',
              }}
            >
              When you secure your bundle during the webinar, you'll also unlock a 30-minute private Momentum Call with an Easy Expert.
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#000000',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: '800px',
              }}
            >
              This session isn't a funnel audit. It's about{' '}
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                clarity, positioning, and momentum
              </Box>
              {' '}—helping you move faster whether you're launching your first webinar or scaling to the next level.
            </Typography>

            {/* Two-Column Feature Section */}
            <Grid container spacing={{ xs: 2, md: 6 }} sx={{ mb: 4 }}>
              {/* Left Column - New? */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: { xs: 3, md: 4 },
                    pt: 0,
                  }}
                  maxWidth="460px"
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '20px', md: '24px' },
                      fontWeight: 'bold',
                      color: '#FF7F50',
                      mb: 2,
                    }}
                  >
                    New?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      color: '#000000',
                      lineHeight: 1.6,
                    }}
                  >
                    We'll cut through the noise and map your first clear wins.
                  </Typography>
                </Box>
              </Grid>

              {/* Right Column - Experienced? */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: { xs: 3, md: 4 },
                    pt: 0,
                  }}
                  maxWidth="460px"
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '20px', md: '24px' },
                      fontWeight: 'bold',
                      color: '#FF7F50',
                      mb: 2,
                    }}
                  >
                    Experienced?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      color: '#000000',
                      lineHeight: 1.6,
                    }}
                  >
                    We'll highlight overlooked opportunities and give you advanced marketing insights to multiply results.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Bottom Call-to-Action Banner */}
            <Box
              sx={{
                backgroundColor: '#FF3700',
                color: 'white',
                p: "11px 70px",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                }}
              >
                Only available as a fast-action bonus when you join live.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Call-to-Action Banner */}
      <Box
        sx={{
          backgroundColor: '#000000',
          width: '100%',
          py: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern - Dots */}
        <Box
          sx={{
            position: 'absolute',
            right: "-18px",
            top: 0,
            bottom: 0,
            width: { xs: '30%', md: '209px' },
            height: { xs: '30%', md: '40%' },
            backgroundImage: `url(${DotPattern})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Headline */}
            <Typography
              sx={{
                fontSize: { xs: '28px', sm: '36px', md: '48px' },
                fontWeight: 'bold',
                color: 'white',
                mb: 1.5,
                lineHeight: 1.2,
              }}
            >
              Ready to Transform Your Webinars?
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: { xs: '16px', sm: '18px', md: '24px' },
                color: 'white',
                mb: 6,
                lineHeight: 1.5,
              }}
            >
              Get everything you need to create webinars that actually convert - today only for $1,297.
            </Typography>

            {/* Call-to-Action Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#FF6B4E',
                color: 'white',
                borderRadius: '50px',
                px: { xs: 4, md: 8 },
                py: { xs: 1, md: 2 },
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                fontWeight: 'bold',
                textTransform: 'none',
                letterSpacing: '0.3px',
                minWidth: { xs: '280px', md: '350px' },
                boxShadow: '0 8px 32px rgba(255, 107, 78, 0.4)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#E55A2B',
                  boxShadow: '0 12px 40px rgba(255, 107, 78, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              }}
              onClick={() => handleOpenModal('professionalBundle')}
            >
              Get Professional Bundle - $1,297
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box bgcolor="#ffffff" width="100%">
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          {/* Top Section - G2 Rating and Headline */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              justifyContent: 'space-between',
              mb: { xs: 6, md: 8 },
              gap: { xs: 4, md: 0 },
            }}
          >
            {/* Left Side - G2 Rating and Headline */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                textAlign: { xs: 'center', md: 'left' },
                maxWidth: { xs: '100%', md: '60%' },
              }}
            >
              {/* G2 Rating */}
              <Box
                component="img"
                src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-with-rating-stars.png"
                alt="G2 Rating"
                sx={{
                  height: { xs: '40px', md: '50px' },
                  width: 'auto',
                  objectFit: 'contain',
                  mb: 3,
                }}
              />

              {/* Headline */}
              <Typography
                sx={{
                  fontSize: { xs: '24px', sm: '28px', md: '36px', lg: '42px' },
                  fontWeight: 'bold',
                  color: '#1F2937',
                  lineHeight: 1.2,
                  maxWidth: '600px',
                }}
              >
                Join the 94% who turn webinars into real results
              </Typography>
            </Box>

            {/* Right Side - G2 Badges */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1, md: 2 },
                justifyContent: { xs: 'center', md: 'flex-end' },
                maxWidth: { xs: '100%', md: '40%' },
              }}
            >
              <Box
                component="img"
                src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-groups-7badges.png"
                alt="G2 Badges"
                sx={{
                  height: { xs: '60px', md: '80px' },
                  width: 'auto',
                  objectFit: 'contain',
                  maxWidth: '100%',
                }}
              />
            </Box>
          </Box>

          {/* Testimonial Cards Section */}
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Card 1 - Amy Porterfield */}
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Box
                sx={{
                  backgroundColor: '#FFF7F7',
                  borderRadius: '20px',
                  border: '1px solid #DEE0ED',
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Video Thumbnail */}
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '120px', md: '140px' },
                    borderRadius: '8px',
                    overflow: 'hidden',
                    mb: 3,
                    position: 'relative',
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .play-button': {
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      },
                    },
                  }}
                  onClick={() => handleOpenVideoModal('https://www.youtube.com/embed/c-rqpqv7xRA')}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.s3.amazonaws.com/images/v3-images/amy-porterfield-video-thumbnail.png"
                    alt="Amy Porterfield Video"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Play Button */}
                  <Box
                    className="play-button"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '12px solid #FF6B35',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        marginLeft: '2px',
                      }}
                    />
                  </Box>
                </Box>

                {/* Quote */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '15px' },
                    lineHeight: 1.5,
                    color: '#374151',
                    mb: 3,
                    flex: 1,
                  }}
                >
                  "I personally use EasyWebinar and they are the best. I moved from using other platforms and nothing compares to their reliability and customer support!"
                </Typography>

                {/* Avatar and Name */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.easywebinar.com/images/amy-porterfield-img.png"
                    alt="Amy Porterfield"
                    sx={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#1F2937',
                        mb: 0.5,
                      }}
                    >
                      Amy Porterfield
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#6B7280',
                      }}
                    >
                      Online Marketing Expert
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Card 2 - Jason Brown */}
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Box
                sx={{
                  backgroundColor: '#FFF7F7',
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease',
                  borderRadius: '20px',
                  border: '1px solid #DEE0ED',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Video Thumbnail */}
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '120px', md: '140px' },
                    borderRadius: '8px',
                    overflow: 'hidden',
                    mb: 3,
                    position: 'relative',
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .play-button': {
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      },
                    },
                  }}
                  onClick={() => handleOpenVideoModal('https://player.vimeo.com/video/992801822')}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.s3.amazonaws.com/images/v3-images/jason-video-thumbnail.png"
                    alt="Jason Brown Video"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Play Button */}
                  <Box
                    className="play-button"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '12px solid #FF6B35',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        marginLeft: '2px',
                      }}
                    />
                  </Box>
                </Box>

                {/* Quote */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '15px' },
                    lineHeight: 1.5,
                    color: '#374151',
                    mb: 3,
                    flex: 1,
                  }}
                >
                  "It's been over 5 years and that speaks for itself. I haven't had to go look for another competitor. I haven't had to go see what else is better out there..."
                </Typography>

                {/* Avatar and Name */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.easywebinar.com/images/jason-brown-centered-profile-pic.png"
                    alt="Jason Brown"
                    sx={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#1F2937',
                        mb: 0.5,
                      }}
                    >
                      Jason Brown
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#6B7280',
                      }}
                    >
                      Stock Market Coach
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Card 3 - Jessica Catorc */}
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Box
                sx={{
                  backgroundColor: '#FFF7F7',
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease',
                  borderRadius: '20px',
                  border: '1px solid #DEE0ED',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Video Thumbnail */}
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '120px', md: '140px' },
                    borderRadius: '8px',
                    overflow: 'hidden',
                    mb: 3,
                    position: 'relative',
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .play-button': {
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      },
                    },
                  }}
                  onClick={() => handleOpenVideoModal('https://player.vimeo.com/video/430032495')}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.easywebinar.com/images/v3-images/jessica-catorc-video-thumbnail.png"
                    alt="Jessica Catorc Video"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Play Button */}
                  <Box
                    className="play-button"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '12px solid #FF6B35',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        marginLeft: '2px',
                      }}
                    />
                  </Box>
                </Box>

                {/* Quote */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '15px' },
                    lineHeight: 1.5,
                    color: '#374151',
                    mb: 3,
                    flex: 1,
                  }}
                >
                  "With EasyWebinar you understand the people you're serving, which means you're thinking of the things they need. It's not just flashy software, it's what is important and what is vital for someone to grow their business."
                </Typography>

                {/* Avatar and Name */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.easywebinar.com/images/v3-images/jassica-60x60-profile.png"
                    alt="Jessica Catorc"
                    sx={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#1F2937',
                        mb: 0.5,
                      }}
                    >
                      Jessica Catorc
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#6B7280',
                      }}
                    >
                      Teachable
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Card 4 - Susie Moore */}
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Box
                sx={{
                  backgroundColor: '#FFF7F7',
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease',
                  borderRadius: '20px',
                  border: '1px solid #DEE0ED',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Video Thumbnail */}
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '120px', md: '140px' },
                    borderRadius: '8px',
                    overflow: 'hidden',
                    mb: 3,
                    position: 'relative',
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .play-button': {
                        transform: 'translate(-50%, -50%) scale(1.1)',
                      },
                    },
                  }}
                  onClick={() => handleOpenVideoModal('https://player.vimeo.com/video/460316313')}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.easywebinar.com/images/v3-images/Sussie-more-video-thumbnail.png"
                    alt="Susie Moore Video"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Play Button */}
                  <Box
                    className="play-button"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '12px solid #FF6B35',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        marginLeft: '2px',
                      }}
                    />
                  </Box>
                </Box>

                {/* Quote */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '15px' },
                    lineHeight: 1.5,
                    color: '#374151',
                    mb: 3,
                    flex: 1,
                  }}
                >
                  "The name is completely self-explanatory. Easiest integrations, best customer service and EASY to use."
                </Typography>

                {/* Avatar and Name */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="https://easywebinar-site.easywebinar.com/images/susie-moore-circle.png"
                    alt="Susie Moore"
                    sx={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#1F2937',
                        mb: 0.5,
                      }}
                    >
                      Susie Moore
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#6B7280',
                      }}
                    >
                      Author/Lifecoach
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer Section */}
      <Box
        sx={{
          width: "100%",
          background: "#000000",
          color: "white",
          py: { xs: 4, md: 6 },
        }}
      >
        {/* Bottom Section - Copyright and Links */}
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          {/* Left Aligned - Copyright */}
          <Typography
            sx={{
              color: "white",
              fontSize: { xs: "14px", md: "16px" },
              fontFamily: "Inter, sans-serif",
            }}
          >
            Copyright © EasyWebinar.com. All rights reserved.
          </Typography>

          {/* Center - Privacy Policy and Terms of Service */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 2 },
              alignItems: { xs: "flex-start", sm: "center" },
            }}
          >
            <Typography
              component="a"
              href="https://easywebinar.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                fontSize: { xs: "14px", md: "16px" },
                fontFamily: "Inter, sans-serif",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                  textDecoration: "underline",
                },
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              sx={{
                color: "white",
                fontSize: { xs: "14px", md: "16px" },
                fontFamily: "Inter, sans-serif",
                display: { xs: "none", sm: "block" },
              }}
            >
              •
            </Typography>
            <Typography
              component="a"
              href="https://easywebinar.com/terms-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                fontSize: { xs: "14px", md: "16px" },
                fontFamily: "Inter, sans-serif",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                  textDecoration: "underline",
                },
              }}
            >
              Terms of Service
            </Typography>
          </Box>

          {/* Right Aligned - Follow us */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                color: "white",
                fontSize: { xs: "14px", md: "16px" },
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
              }}
            >
              Follow us
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {/* Facebook Icon */}
              <Box
                component="a"
                href="https://www.facebook.com/easywebinar/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "opacity 0.3s ease",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M26.134 11.0391H23.3638C22.1393 11.0391 20.9649 11.5741 20.0991 12.5266C19.2332 13.479 18.7468 14.7708 18.7468 16.1178V19.165H15.9766V23.228H18.7468V31.3539H22.4404V23.228H25.2106L26.134 19.165H22.4404V16.1178C22.4404 15.8484 22.5377 15.59 22.7108 15.3995C22.884 15.209 23.1189 15.102 23.3638 15.102H26.134V11.0391Z" stroke="white" stroke-width="1.6929" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </Box>

              {/* Instagram Icon */}
              <Box
                component="a"
                href="https://www.instagram.com/easywebinar/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "opacity 0.3s ease",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <svg width="41" height="42" viewBox="0 0 41 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M16.3943 12.8921C14.1904 12.8921 12.4038 14.6787 12.4038 16.8825V26.5563C12.4038 28.7601 14.1904 30.5467 16.3943 30.5467H26.068C28.2718 30.5467 30.0584 28.7601 30.0584 26.5563V16.8825C30.0584 14.6787 28.2718 12.8921 26.068 12.8921H16.3943ZM10.7109 16.8825C10.7109 13.7437 13.2554 11.1992 16.3943 11.1992H26.068C29.2068 11.1992 31.7513 13.7437 31.7513 16.8825V26.5563C31.7513 29.6951 29.2068 32.2396 26.068 32.2396H16.3943C13.2554 32.2396 10.7109 29.6951 10.7109 26.5563V16.8825ZM21.7161 18.6847C21.0871 18.5914 20.4448 18.6988 19.8804 18.9917C19.316 19.2845 18.8583 19.7479 18.5724 20.3159C18.2865 20.8838 18.187 21.5275 18.288 22.1553C18.3891 22.7831 18.6855 23.363 19.1351 23.8126C19.5847 24.2622 20.1646 24.5586 20.7924 24.6597C21.4202 24.7607 22.0639 24.6612 22.6318 24.3753C23.1998 24.0894 23.6632 23.6317 23.956 23.0673C24.2489 22.5029 24.3563 21.8606 24.263 21.2316C24.1679 20.59 23.8689 19.996 23.4103 19.5374C22.9517 19.0788 22.3577 18.7798 21.7161 18.6847ZM19.1007 17.489C19.9811 17.0322 20.9832 16.8646 21.9644 17.0101C22.9653 17.1585 23.8919 17.6249 24.6074 18.3403C25.3228 19.0558 25.7892 19.9824 25.9376 20.9833C26.0831 21.9645 25.9155 22.9666 25.4587 23.847C25.0018 24.7275 24.279 25.4415 23.3929 25.8875C22.5069 26.3334 21.5028 26.4886 20.5235 26.3311C19.5441 26.1735 18.6394 25.7111 17.938 25.0097C17.2366 24.3083 16.7742 23.4036 16.6166 22.4242C16.4591 21.4449 16.6143 20.4408 17.0602 19.5548C17.5062 18.6687 18.2202 17.9459 19.1007 17.489ZM26.5555 15.5505C26.088 15.5505 25.709 15.9295 25.709 16.397C25.709 16.8644 26.088 17.2434 26.5555 17.2434H26.5647C27.0322 17.2434 27.4111 16.8644 27.4111 16.397C27.4111 15.9295 27.0322 15.5505 26.5647 15.5505H26.5555Z" fill="white"></path>
                </svg>
              </Box>

              {/* LinkedIn Icon */}
              <Box
                component="a"
                href="https://www.linkedin.com/company/easywebinar"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "opacity 0.3s ease",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.6929 12.3232C12.6929 11.6935 13.2272 11.1382 13.9424 11.1382C14.6577 11.1382 15.192 11.6935 15.192 12.3232C15.192 12.953 14.6577 13.5083 13.9424 13.5083C13.2272 13.5083 12.6929 12.953 12.6929 12.3232ZM13.9424 9.44531C12.3425 9.44531 11 10.7091 11 12.3232C11 13.9374 12.3425 15.2012 13.9424 15.2012C15.5424 15.2012 16.8849 13.9374 16.8849 12.3232C16.8849 10.7091 15.5424 9.44531 13.9424 9.44531ZM26.5195 17.2287C25.068 17.2287 23.681 17.7878 22.6624 18.7751C21.6447 19.7615 21.0781 21.0935 21.0781 22.4767V28.7404H23.5771V22.4767C23.5771 21.7047 23.8938 20.9699 24.4484 20.4324C25.0021 19.8958 25.7475 19.5988 26.5195 19.5988C27.2915 19.5988 28.037 19.8958 28.5907 20.4324C29.1453 20.9699 29.462 21.7047 29.462 22.4767V28.7404H31.961V22.4767C31.961 21.0935 31.3944 19.7615 30.3767 18.7751C29.3581 17.7878 27.9711 17.2287 26.5195 17.2287ZM26.5195 15.5358C24.6358 15.5358 22.824 16.2608 21.4842 17.5595C20.1435 18.859 19.3852 20.6272 19.3852 22.4767V29.5869C19.3852 30.0544 19.7641 30.4333 20.2316 30.4333H24.4236C24.891 30.4333 25.27 30.0544 25.27 29.5869V22.4767C25.27 22.1711 25.395 21.8725 25.6266 21.648C25.859 21.4227 26.1798 21.2917 26.5195 21.2917C26.8593 21.2917 27.1801 21.4227 27.4125 21.648C27.6441 21.8725 27.7691 22.1711 27.7691 22.4767V29.5869C27.7691 30.0544 28.148 30.4333 28.6155 30.4333H32.8075C33.275 30.4333 33.6539 30.0544 33.6539 29.5869V22.4767C33.6539 20.6272 32.8956 18.859 31.5549 17.5595C30.215 16.2608 28.4033 15.5358 26.5195 15.5358ZM11 17.4006C11 16.9332 11.379 16.5542 11.8465 16.5542H16.0384C16.5059 16.5542 16.8849 16.9332 16.8849 17.4006V29.5895C16.8849 30.057 16.5059 30.436 16.0384 30.436H11.8465C11.379 30.436 11 30.057 11 29.5895V17.4006ZM12.6929 18.2471V28.7431H15.192V18.2471H12.6929Z" fill="white"></path>
                </svg>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Purchase Modal */}
      <PurchaseModal
        open={modalOpen}
        onClose={handleCloseModal}
        planTitle={modalPlan === 'professionalBundle' ? planData.professionalBundle.modalTile : planData.auditBundle.modalTile}
        planPrice={modalPlan === 'professionalBundle' ? planData.professionalBundle.price : planData.auditBundle.price}
        planType={modalPlan}
        isNonAttendee={isNonAttendee}
      />

      {/* Video Modal */}
      {videoModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2,
          }}
          onClick={handleCloseVideoModal}
        >
          <Box
            sx={{
              position: 'relative',
              width: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
              maxWidth: '800px',
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: '32px',
                height: '32px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
              onClick={handleCloseVideoModal}
            >
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  position: 'relative',
                  '&::before, &::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '12px',
                    height: '2px',
                    backgroundColor: 'white',
                    transformOrigin: 'center',
                  },
                  '&::before': {
                    transform: 'translate(-50%, -50%) rotate(45deg)',
                  },
                  '&::after': {
                    transform: 'translate(-50%, -50%) rotate(-45deg)',
                  },
                }}
              />
            </Box>

            {/* Video Container */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 0,
                paddingBottom: '56.25%', // 16:9 aspect ratio
                backgroundColor: '#000',
              }}
            >
              <Box
                component="iframe"
                src={videoUrl}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default LiveAuditWorkshopOffer;
